import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { OSIndicator } from '@/lib/os-indicator';
import { OSAvatarIndicator } from './OSAvatarIndicator';

interface ClienteListItemProps {
  id: string;
  nome: string;
  avatarUrl?: string | null;
  codigo?: string | null;
  osIndicador?: OSIndicator | null;
  preview?: string;
  previewTipoMedia?: string | null;
  previewTime?: string;
  unreadCount?: number;
  isSelected?: boolean;
  onClick?: () => void;
}

function previewLabel(
  preview: string | undefined,
  tipoMedia: string | null | undefined,
  t: (k: string, def?: string) => string,
): string {
  if (!preview && !tipoMedia) return t('panel.noPreview', 'Sem mensagens ainda');
  if (tipoMedia && !preview) {
    switch (tipoMedia) {
      case 'image':
        return `📷 ${t('panel.mediaImage', 'Imagem')}`;
      case 'audio':
        return `🎙 ${t('panel.mediaAudio', 'Áudio')}`;
      case 'video':
        return `🎬 ${t('panel.mediaVideo', 'Vídeo')}`;
      case 'document':
        return `📎 ${t('panel.mediaDocument', 'Documento')}`;
    }
  }
  return preview || '';
}

function formatRelativeShort(iso: string | undefined): string {
  if (!iso) return '';
  try {
    return formatDistanceToNowStrict(new Date(iso), { locale: ptBR, addSuffix: false })
      // Compact: "5 minutos" → "5m", "2 horas" → "2h", "3 dias" → "3d"
      .replace(/\s*minutos?/, 'm')
      .replace(/\s*horas?/, 'h')
      .replace(/\s*dias?/, 'd')
      .replace(/\s*meses?/, 'mes')
      .replace(/\s*anos?/, 'a')
      .replace(/\s*segundos?/, 's');
  } catch {
    return '';
  }
}

export const ClienteListItem = React.forwardRef<
  HTMLButtonElement,
  ClienteListItemProps
>(function ClienteListItem(
  {
    nome,
    avatarUrl,
    codigo,
    osIndicador = null,
    preview,
    previewTipoMedia,
    previewTime,
    unreadCount = 0,
    isSelected = false,
    onClick,
  },
  ref,
) {
  const { t } = useTranslation();
  const inicial = nome?.charAt(0)?.toUpperCase() || '?';
  const previewText = previewLabel(preview, previewTipoMedia, t);
  const timeText = formatRelativeShort(previewTime);

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={`cli-itm${isSelected ? ' is-active' : ''}`}
    >
      <div className="cli-itm__avt">
        {avatarUrl ? (
          <img src={avatarUrl} alt={nome || ''} className="cli-itm__avt-img object-cover" />
        ) : (
          <span className="cli-itm__avt-img">{inicial}</span>
        )}
        <OSAvatarIndicator osIndicador={osIndicador} ledClassName="cli-itm__led" />
      </div>
      <div className="cli-itm__body">
        <div className="cli-itm__top">
          <span className="cli-itm__name">{nome}</span>
          {timeText && <span className="cli-itm__time">{timeText}</span>}
        </div>
        <div className="cli-itm__bot">
          {codigo && <span className="cli-itm__code">{codigo}</span>}
          {unreadCount > 0 && (
            <span className="cli-itm__badge" aria-label={`${unreadCount} novas`}>
              {unreadCount}
            </span>
          )}
        </div>
        <div className="cli-itm__preview">{previewText}</div>
      </div>
    </button>
  );
});

ClienteListItem.displayName = 'ClienteListItem';
